import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
} from "@mui/material";
import apiClient from "../../../shared/apiClient";
import { AddressInput } from "./AddressInput";
import { StrataPlanInput } from "./StrataPlanInput";

interface AddPropertyModalProps {
  open: boolean;
  onClose: () => void;
}

export const AddPropertyModal: React.FC<AddPropertyModalProps> = ({
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState("");
  const [strataPlanNumber, setStrataPlanNumber] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    // Client-side validation
    if (!address && !strataPlanNumber) {
      setError("Either address or strata plan number is required");
      return;
    }

    setLoading(true);

    try {
      await apiClient.post("/properties", {
        address,
        strataPlanNumber: strataPlanNumber || undefined, // Only send if non-empty
      });

      setAddress("");
      setStrataPlanNumber("");
      onClose();
    } catch (err) {
      // Extract error message from the API response
      if (err && typeof err === "object" && "response" in err) {
        const response = (err as any).response;
        if (response?.data?.error?.message) {
          setError(response.data.error.message);
        } else {
          setError("Failed to create property");
        }
      } else {
        setError("Failed to create property");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add Property</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <AddressInput
              value={address}
              onChange={setAddress}
              disabled={loading}
            />
            <StrataPlanInput
              value={strataPlanNumber}
              onChange={setStrataPlanNumber}
              disabled={loading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
            data-testid="cancel-add-property-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || (!address && !strataPlanNumber)}
            data-testid="submit-add-property-button"
          >
            {loading ? "Adding..." : "Add Property"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
